import React, { useEffect, useState, Children } from 'react';
import { Header } from '../components/Header';
import { VideoPlayer } from '../components/VideoPlayer';
import { StatsSection } from '../components/StatsSection';
import { HeroHighlight, Highlight } from '../components/ui/HeroHighlight';
import { CalendlyEmbed } from '../components/CalendlyEmbed';
import { FAQSection } from '../components/FAQSection';
import { Footer } from '../components/Footer';
import { BookCallButton } from '../components/ui/BookCallButton';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
export function CulturePage() {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const scrollToCalendly = () => {
    const element = document.getElementById('calendly');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleNextStep = () => {
    setIsDialogOpen(true);
  };
  const handleConfirm = () => {
    setIsDialogOpen(false);
    navigate('/dns');
  };
  // Animation variants
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1,
        duration: 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        damping: 12,
        stiffness: 100,
        duration: 0.4
      }
    }
  };
  return <div className="w-full min-h-screen" data-id="element-123">
      <HeroHighlight data-id="element-124">
        <Header data-id="element-125" />
        <motion.main className="max-w-6xl mx-auto px-4" initial="hidden" animate="visible" variants={containerVariants} data-id="element-126">
          <motion.div id="headline" variants={itemVariants} data-id="element-127">
            <div className="flex justify-center mb-4 sm:mb-6 items-center" data-id="element-128">
              <div className="flex items-center" data-id="element-129">
                <motion.button onClick={() => navigate('/onboarding')} className="mr-2 sm:mr-3 h-8 w-8 sm:h-9 sm:w-9 flex items-center justify-center rounded-full bg-white/5 backdrop-blur-sm border border-white/10 text-white hover:bg-white/10 transition-colors" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} data-id="element-130">
                  <ChevronLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-131" />
                </motion.button>
                <div className="inline-flex items-center px-3 sm:px-6 py-1.5 sm:py-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm" data-id="element-132">
                  <span className="text-xs sm:text-sm md:text-base font-medium text-white" data-id="element-133">
                    STEP
                  </span>
                  <div className="inline-flex items-center justify-center w-5 h-5 sm:w-7 sm:h-7 mx-1 sm:mx-2 rounded-full bg-gradient-to-r from-blue-400 to-indigo-600" data-id="element-134">
                    <span className="text-xs sm:text-base font-bold text-white" data-id="element-135">
                      3
                    </span>
                  </div>
                  <span className="text-xs sm:text-sm md:text-base font-medium text-white" data-id="element-136">
                    OF 5
                  </span>
                </div>
                <motion.button onClick={handleNextStep} className="ml-2 sm:ml-3 h-8 w-8 sm:h-9 sm:w-9 flex items-center justify-center rounded-full bg-white/5 backdrop-blur-sm border border-white/10 text-white hover:bg-white/10 transition-colors" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} data-id="element-137">
                  <ChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-138" />
                </motion.button>
              </div>
            </div>
            <h1 className="text-center text-2xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8 max-w-4xl mx-auto leading-[1.4] sm:leading-[1.3] text-white" data-id="element-139">
              Watch the video below before beginning your{' '}
              <Highlight data-id="element-140">culture and opportunity interview</Highlight>
            </h1>
            <div className="mb-10 sm:mb-12" data-id="element-141">
              <p className="text-center text-base sm:text-xl mb-4 sm:mb-6 text-white" data-id="element-142">
                <strong data-id="element-143">
                  Tap play on the video below (Watch at 1.5x speed for faster
                  efficiency)
                </strong>
              </p>
              <VideoPlayer mediaId="r26icmni2h" data-id="element-144" />
            </div>
          </motion.div>
          <motion.div id="calendly" className="text-center mb-6" variants={itemVariants} data-id="element-145">
            <p className="text-center text-xl font-semibold text-white mb-6" data-id="element-146">
              Step 1: Begin Your Culture and Opportunity Interview
            </p>
            <div className="flex justify-center mb-8" data-id="element-147">
              <motion.button onClick={() => window.open('https://video.thehireadvantage.co/fqsfoa2dn', '_blank')} className="py-4 px-10 text-xl sm:text-2xl transform hover:scale-105 bg-gradient-to-r from-blue-400/90 to-indigo-600/90 hover:from-blue-400 hover:to-indigo-600 text-white font-bold rounded-xl transition-all duration-300 shadow-xl" whileHover={{
              scale: 1.05,
              boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.5)'
            }} whileTap={{
              scale: 0.98
            }} initial={{
              opacity: 1,
              scale: 1
            }} data-id="element-148">
                Begin Interview
              </motion.button>
            </div>
            <p className="text-center text-xl font-semibold text-white mb-6" data-id="element-149">
              Step 2: Click The Button Below To Continue
            </p>
            <div className="flex justify-center mb-8" data-id="element-150">
              <div className="w-full max-w-md" data-id="element-151">
                <BookCallButton onClick={handleNextStep} className="py-4 px-10 text-xl sm:text-2xl" data-id="element-152" />
              </div>
            </div>
          </motion.div>
          <motion.div variants={itemVariants} data-id="element-153">
            <CalendlyEmbed data-id="element-154" />
          </motion.div>
          <motion.div id="stats" variants={itemVariants} data-id="element-155">
            <StatsSection data-id="element-156" />
          </motion.div>
          <motion.div id="faq" variants={itemVariants} data-id="element-157">
            <FAQSection data-id="element-158" />
          </motion.div>
          <Footer data-id="element-159" />
        </motion.main>
      </HeroHighlight>
      <ConfirmationDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} onConfirm={handleConfirm} title="Did you fully complete this step?" message="You need to complete this step before proceeding to the next one." data-id="element-160" />
    </div>;
}